import { Observable, of } from 'rxjs';
import { StatusWidgetDataDTO, UserStatusWidgetDataDTO, UserStatusDTO } from '../../../dto/dashboard/dashboard.dto';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { postDashboardListData } from '../dashboard-data-api';
import { ListRequestDTO, createListRequest } from '../../../dto/list-request.dto';

/**
 * Data source for the Status widget
 * Provides status information about various metrics
 */
export const getStatusData = (): Observable<StatusWidgetDataDTO> => {
  // Static data for now - will be replaced by API calls
  return of({
    percentComplete: 75,
    totalUsers: 120,
    completedUsers: 90,
    totalEvents: 45,
    completedEvents: 30,
    statusText: 'På rette vej',
    lastUpdate: new Date().toISOString()
  });
};

/**
 * Get status data for a specific category
 */
export const getStatusByCategory = (category: string): Observable<StatusWidgetDataDTO> => {
  // Status data by category
  const statusByCategory: Record<string, StatusWidgetDataDTO> = {
    'education': {
      percentComplete: 82,
      totalUsers: 78,
      completedUsers: 64,
      totalEvents: 32,
      completedEvents: 28,
      statusText: 'Uddannelser kører planmæssigt',
      lastUpdate: new Date().toISOString()
    },
    'exercises': {
      percentComplete: 65,
      totalUsers: 95,
      completedUsers: 62,
      totalEvents: 18,
      completedEvents: 11,
      statusText: 'Flere øvelser igangsat',
      lastUpdate: new Date().toISOString()
    },
    'certification': {
      percentComplete: 91,
      totalUsers: 42,
      completedUsers: 38,
      totalEvents: 12,
      completedEvents: 10,
      statusText: 'Certificeringer næsten færdige',
      lastUpdate: new Date().toISOString()
    },
    'equipment': {
      percentComplete: 58,
      totalUsers: 0,
      completedUsers: 0,
      totalEvents: 35,
      completedEvents: 20,
      statusText: 'Udstyrsvedligehold i gang',
      lastUpdate: new Date().toISOString()
    },
    'default': {
      percentComplete: 75,
      totalUsers: 120,
      completedUsers: 90,
      totalEvents: 45,
      completedEvents: 30,
      statusText: 'På rette vej',
      lastUpdate: new Date().toISOString()
    }
  };

  return of(statusByCategory[category] || statusByCategory['default']);
};

/**
 * Get user status data for the user status widget
 * @param http - HttpClient instance
 * @param filters - Optional filters to apply
 * @returns Observable of user status data aggregated for the widget
 */
export function getUserStatusData(
  http: HttpClient,
  filters: any = {}
): Observable<UserStatusWidgetDataDTO> {
  
  const listRequest: ListRequestDTO = createListRequest({
    page: 1,
    pageSize: 1000,
    filters,
    includeFilters: true,
  });

  return postDashboardListData(http, 'userStatus', listRequest).pipe(
    map((response) => {
      const users = response.data || [];
      
      // Count users in different statuses
      let completed = 0;
      let inProgress = 0;
      let notStarted = 0;
      
      // Process user data and count statuses
      users.forEach((user: UserStatusDTO & { status?: Record<string, number> }) => {
        const summary = user.summary ?? (user.status
          ? {
              completedPaths: user.status['completed_paths'] ?? user.status['completedPaths'] ?? 0,
              inProgressPaths: user.status['in_progress_paths'] ?? user.status['inProgressPaths'] ?? 0,
              notStartedPaths: user.status['not_started_paths'] ?? user.status['notStartedPaths'] ?? 0,
            }
          : null);
        if (summary) {
          if (summary.completedPaths > 0) {
            completed++;
          }
          if (summary.inProgressPaths > 0) {
            inProgress++;
          }
          if (summary.notStartedPaths > 0) {
            notStarted++;
          }
        }
      });
      
      // Calculate total from the different status counts
      // Important: users can have multiple paths, so we must count them based on having any paths
      const totalUsers = users.length;
      
      // Calculate percentage complete
      const percentComplete = totalUsers > 0 ? Math.round((completed / totalUsers) * 100) : 0;
      
      // Create a properly structured response
      const result: UserStatusWidgetDataDTO = {
        completedUsers: completed,
        totalUsers: totalUsers,
        percentComplete: percentComplete,
        inProgressUsers: inProgress,
        notStartedUsers: notStarted,
        statusDistribution: [
          { status: 'completed', count: completed },
          { status: 'in-progress', count: inProgress },
          { status: 'not-started', count: notStarted }
        ]
      };
      
      return result;
    }),
    catchError(error => {
      console.error(`[USER-STATUS-DATA] API error:`, error);
      
      // Return mock data in case of error
      return of(generateMockUserStatusData());
    })
  );
}

/**
 * Generate mock user status data for testing and fallback
 */
function generateMockUserStatusData(): UserStatusWidgetDataDTO {
  const completed = 8;
  const inProgress = 5;
  const notStarted = 3;
  const total = completed + inProgress + notStarted;
  
  return {
    completedUsers: completed,
    totalUsers: total,
    percentComplete: Math.round((completed / total) * 100),
    inProgressUsers: inProgress,
    notStartedUsers: notStarted,
    statusDistribution: [
      { status: 'completed', count: completed },
      { status: 'in-progress', count: inProgress },
      { status: 'not-started', count: notStarted }
    ]
  };
}

/**
 * Get historical status data for trending
 */
export const getStatusHistory = (): Observable<StatusWidgetDataDTO[]> => {
  // Generate some historical data points
  const now = new Date();
  const history: StatusWidgetDataDTO[] = [];
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Generate somewhat realistic trending data
    const basePercent = 70; // Starting percentage
    const volatility = 5;   // How much it can vary
    const trend = 1;        // Upward trend

    const randomVariance = (Math.random() * volatility * 2) - volatility;
    const trendFactor = i * trend;
    const percentComplete = Math.min(100, Math.max(0, basePercent + randomVariance + trendFactor));
    
    history.push({
      percentComplete,
      totalUsers: 120,
      completedUsers: Math.round(120 * (percentComplete / 100)),
      totalEvents: 45,
      completedEvents: Math.round(45 * (percentComplete / 100)),
      statusText: percentComplete > 85 ? 'Meget tilfredsstillende' : 
                  percentComplete > 70 ? 'Tilfredsstillende' : 
                  percentComplete > 50 ? 'Under udvikling' : 'Kræver opmærksomhed',
      lastUpdate: date.toISOString()
    });
  }
  
  return of(history);
}; 